import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { FormsModule, UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { APIService } from '../../service/api.service';
import { failIfSwallowed } from '../../util/swallowed-http';

interface BoardingLocation {
  id: string;
  active: boolean;
  name: { ko: string; en: string; ja: string; 'zh-Hant'?: string };
}
interface Catalog {
  revision: string;
  locationsJson: string;
}

@Component({
  selector: 'product-boarding',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './product-boarding.component.html',
})
export class ProductBoardingComponent implements OnChanges, OnDestroy {
  @Input() productId: number;
  @Input() form?: UntypedFormGroup;
  locations: BoardingLocation[] = [];
  persistedIds = new Set<string>();
  revision: string;
  loading = false;
  saving = false;
  dirty = false;
  message = '';
  serverMessage = '';
  private request?: Subscription;

  constructor(private api: APIService) {}

  ngOnChanges() {
    if (this.form && !this.form.contains('boardingLocationIds'))
      this.form.addControl('boardingLocationIds', new UntypedFormControl(undefined));
    if (!this.productId) return;
    this.request?.unsubscribe();
    this.locations = [];
    this.revision = undefined;
    this.loading = true;
    this.message = '';
    this.serverMessage = '';
    this.request = this.api
      .get<Catalog>(`/api/products/${this.productId}/boarding-locations`)
      .pipe(failIfSwallowed())
      .subscribe({
        next: (catalog) => this.loaded(catalog),
        error: () => {
          this.loading = false;
          this.message = 'BOARDING.LOAD_ERROR';
        },
      });
  }

  ngOnDestroy() {
    this.request?.unsubscribe();
  }

  private loaded(catalog: Catalog) {
    this.locations = JSON.parse(catalog.locationsJson);
    this.persistedIds = new Set(this.locations.map((row) => row.id));
    this.revision = catalog.revision;
    this.loading = false;
    this.dirty = false;
  }

  get selected(): string[] | null | undefined {
    return this.form?.get('boardingLocationIds')?.value;
  }
  get canConvert(): boolean {
    const rows = this.form?.get('boardingLocations')?.value ?? [];
    return rows.every((row: { location: string }) =>
      this.locations.some((item) => item.active && item.name.ko === row.location),
    );
  }

  convert() {
    if (!this.canConvert) return;
    const rows = this.form.get('boardingLocations').value as { location: string }[];
    this.form
      .get('boardingLocationIds')
      .setValue(rows.map((row) => this.locations.find((item) => item.name.ko === row.location).id));
    this.form.markAsDirty();
  }

  toggle(id: string, checked: boolean) {
    const ids = checked ? [...(this.selected ?? []), id] : (this.selected ?? []).filter((item) => item !== id);
    this.form.get('boardingLocationIds').setValue(ids);
    this.form.setControl(
      'boardingLocations',
      new UntypedFormArray(
        ids.map(
          (item) =>
            new UntypedFormGroup({
              location: new UntypedFormControl(this.locations.find((row) => row.id === item).name.ko),
            }),
        ),
      ),
    );
    this.form.markAsDirty();
  }

  add() {
    this.locations = [...this.locations, { id: crypto.randomUUID(), active: true, name: { ko: '', en: '', ja: '' } }];
    this.dirty = true;
  }

  remove(id: string) {
    this.locations = this.locations.filter((row) => row.id !== id);
    this.dirty = true;
  }

  save() {
    if (!this.revision || this.saving || this.locations.some((row) => !row.name.ko.trim())) return;
    this.saving = true;
    this.message = '';
    this.serverMessage = '';
    this.request = this.api
      .put<Catalog>(`/api/products/${this.productId}/boarding-locations`, {
        expectedRevision: this.revision,
        locationsJson: JSON.stringify(this.locations),
      })
      .pipe(failIfSwallowed())
      .subscribe({
        next: (catalog) => {
          this.loaded(catalog);
          this.saving = false;
          this.message = 'BOARDING.SAVED';
        },
        error: (error) => {
          this.saving = false;
          const message = error?.error?.message;
          this.serverMessage = typeof message === 'string' ? message.trim() : '';
          this.message = this.serverMessage ? '' : 'BOARDING.SAVE_ERROR';
        },
      });
  }
}
